
import { useArtistsList } from '@/store/artistsList';
import { useMetrics } from '@/store/metrics';
import { usePageStatus } from '@/store/pageStatus';
import { debounce } from '@/utils';
import { mapActions, mapState, storeToRefs } from 'pinia';
import ValueSlider from './metrics/ValueSlider.vue';
import FlagLabel from './metrics/FlagLabel.vue';
import MetricsSelector from './MetricsSelector.vue';
import KeyboardEvents from './helpers/KeyboardEvents.vue';
import { Metric } from '@/types/metrics';
import { defineComponent } from 'vue';
import { ScoreFilterWithColor } from './MetricsSelector.vue';


const __default__ = defineComponent({
  name: 'SearchSection',
  data () {
    return {
      text: "" as string,
      loading: false as boolean,
      filtersPanelActive: false as boolean,
      suggestedMetricsPanelActive: false as boolean,
      selectedFilters: {} as {[key: string]: ScoreFilterWithColor},
      suggestedMetrics: [] as Array<Metric>,
    }
  },
  setup() {
    const pageStatus = usePageStatus();
    const { artistsLikeThisName } = storeToRefs(pageStatus);

    return {
      artistsLikeThisName,
    };
  },
  components: {
    "metrics-selector": MetricsSelector,
    "value-slider": ValueSlider,
    "flag-label": FlagLabel,
    "keyboard-events": KeyboardEvents,
  },
  async mounted () {
    this.loading = true
    await this.fetchMetrics().catch((error) => {console.log(error); this.setError("Unable to fetch metrics: " + error.message)}).finally(
      () => {
        this.loading = false
        // Suggested filters won't be displayed if the metrics are loading.
        this.getSuggestedFilters()
      }
    )
  },
  methods: {
    ...mapActions(usePageStatus, ['startSearch', 'showNewArtist', 'setArtistLikeThisName', 'setError']),
    ...mapActions(useArtistsList, ['fetchArtists']),
    ...mapActions(useMetrics, ['fetchMetrics']),
    async search () {
      if (this.loading || !this.bannerDismissed)
        return

      this.setArtistLikeThisName("")
      this.toggleFiltersPanel(true)
      this.loading = true

      // Either search by metric or by text.
      if (Object.keys(this.selectedFilters).length > 0)
        this.text = ""

      await this.fetchArtists(this.text, Object.values(this.selectedFilters).map((filter) => {return filter.filter})).catch((error) => {console.log(error); this.setError("Unable to fetch artists: " + error.message)})

      this.startSearch()
      this.loading = false
      this.deactivateSuggestedMetricsPanel()
    },
    async searchOnEnter() {
      if (!this.headerMinimized || this.suggestedMetricsPanelActive || this.filtersPanelActive)
        await this.search()
    },
    toggleFiltersPanel(value: boolean | null = null) {
      // When the selected filters are empty the panel can only be switched off.
      if ((this.filtersPanelActive && !value) || Object.keys(this.selectedFilters).length > 0 || this.artistsLikeThisName) {
        if (value !== null)
          this.filtersPanelActive = value
        else
          this.filtersPanelActive = !this.filtersPanelActive
      }
    },
    getSuggestedFilters() {
      this.suggestedMetrics = this.metrics.filter((metric: Metric) => { return metric.name.toLowerCase().includes(this.text.trim().toLowerCase()) })
    },
    getAndDisplaySuggestedFilters() {
      debounce(
        () => {
          this.getSuggestedFilters()
        },
        300
      )()
      this.activateSuggestedMetricsPanel()
    },
    addFilter(metricWithValue: ScoreFilterWithColor) {
      this.selectedFilters[metricWithValue.filter.metric.id] = metricWithValue
    },
    removeFilter(metricWithValue: ScoreFilterWithColor) {
      delete this.selectedFilters[metricWithValue.filter.metric.id]
      if (Object.keys(this.selectedFilters).length === 0)
        this.filtersPanelActive = false
    },
    activateSuggestedMetricsPanel () {
      if (!this.loading) {
        this.suggestedMetricsPanelActive = true
      }
    },
    deactivateSuggestedMetricsPanel () {
      debounce(
        () => {
          this.suggestedMetricsPanelActive = false
        },
        100
      )()
    },
  },
  computed: {
    ...mapState(useMetrics, ['metrics']),
    ...mapState(usePageStatus, ['headerMinimized', 'artistsLikeThisName', 'bannerDismissed']),
    showSuggestedMetricsPanel(): boolean {
      return this.suggestedMetricsPanelActive
    },
    showFiltersPanel(): boolean {
      return !this.showSuggestedMetricsPanel && this.filtersPanelActive
    },
    searchOutlineColor(): string {
      return this.suggestedMetricsPanelActive ? "var(--darkyellow)" : "var(--darkred)"
    },
    selectionMetrics(): ScoreFilterWithColor[] {
      return [
        ...Object.values(this.selectedFilters),
        ...this.suggestedMetrics.filter(
          (metric: Metric) => {
            return !(metric.id in this.selectedFilters)
          }
        ).map(
          (metric: Metric) => {
            return {
              filter: {
                metric: metric,
                filterValues: {minValue: 0, maxValue: 5}
              },
              selected: false,
              range: true,
            }
          }
        )
      ]
    }
  },
  watch: {
    artistsLikeThisName(newValue, oldValue) {
      if (newValue) {
        this.selectedFilters = {}
        this.toggleFiltersPanel(true)
      } else if (!newValue && oldValue) {
        this.toggleFiltersPanel(false)
      }
    }
  }
});


import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "bae9702e": (_ctx.searchOutlineColor)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__